import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import UpgradeModal from "../Components/UpgradeModal";
import {
  DEFAULT_UPGRADE_REDIRECT,
  getUpgradeDescription,
} from "../constants/lockedFeatures";

const UpgradeModalContext = createContext(null);

export function UpgradeModalProvider({ children }) {
  const navigate = useNavigate();
  const [modal, setModal] = useState({
    open: false,
    title: "",
    description: "",
    redirectUrl: DEFAULT_UPGRADE_REDIRECT,
  });

  const closeUpgradeModal = useCallback(() => {
    setModal((prev) => ({ ...prev, open: false }));
  }, []);

  const openUpgradeModal = useCallback(
    ({ title, featureName, description, redirectUrl = DEFAULT_UPGRADE_REDIRECT }) => {
      if (!featureName && !title) return;
      const resolvedTitle = title || `Unlock ${featureName}`;
      setModal({
        open: true,
        title: resolvedTitle,
        description:
          description ||
          (featureName ? getUpgradeDescription(featureName) : ""),
        redirectUrl,
      });
    },
    []
  );

  const handleLockedClick = useCallback(
    (featureName, options = {}) => {
      openUpgradeModal({
        title: `Unlock ${featureName}`,
        featureName,
        ...options,
      });
    },
    [openUpgradeModal]
  );

  const handleUpgrade = useCallback(() => {
    const url = modal.redirectUrl || DEFAULT_UPGRADE_REDIRECT;
    closeUpgradeModal();
    navigate(url);
  }, [modal.redirectUrl, closeUpgradeModal, navigate]);

  const value = useMemo(
    () => ({
      openUpgradeModal,
      closeUpgradeModal,
      handleLockedClick,
      isUpgradeModalOpen: modal.open,
    }),
    [openUpgradeModal, closeUpgradeModal, handleLockedClick, modal.open]
  );

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      <UpgradeModal
        open={modal.open}
        title={modal.title}
        description={modal.description}
        onClose={closeUpgradeModal}
        onUpgrade={handleUpgrade}
      />
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal() {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    throw new Error("useUpgradeModal must be used within UpgradeModalProvider");
  }
  return ctx;
}
